import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLock, IconLockUser, IconUsers } from '@components/icons';
import { Input } from '@components/input';
import { SectionDisclosure } from '@components/section-disclosure';
import { SelectionCard, SelectionCardSize } from '@components/selection-card';
import { ToggleSection } from '@components/toggle-section';
import { ToggleSectionSize } from '@components/toggle-section/src/toggle-section.type';
import { LG_VIEWPORT, SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

import { ChannelLimitFields } from './components/channel-limit-fields';
import {
  DISTRIBUTION_MODE_OFFLINE,
  DISTRIBUTION_MODE_ONLINE,
  DISTRIBUTION_MODE_ONLINE_AND_OFFLINE,
  DistributionMode,
  DistributionSectionProps,
} from './distribution-section.type';

export const DISTRIBUTION_SECTION_TEST_ID = 'distribution-section';

const ICON_CLASS = 'w-6 h-6';
const RADIO_GROUP_NAME = 'distribution-mode';

interface ModeOption {
  mode: DistributionMode;
  icon: ReactNode;
  titleKey: string;
  descriptionKey: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: DISTRIBUTION_MODE_ONLINE,
    icon: <IconUsers className={ICON_CLASS} />,
    titleKey: 'offers:distributionOnlineTitle',
    descriptionKey: 'offers:distributionOnlineDescription',
  },
  {
    mode: DISTRIBUTION_MODE_OFFLINE,
    icon: <IconLock className={ICON_CLASS} />,
    titleKey: 'offers:distributionOfflineTitle',
    descriptionKey: 'offers:distributionOfflineDescription',
  },
  {
    mode: DISTRIBUTION_MODE_ONLINE_AND_OFFLINE,
    icon: <IconLockUser className={ICON_CLASS} />,
    titleKey: 'offers:distributionOnlineAndOfflineTitle',
    descriptionKey: 'offers:distributionOnlineAndOfflineDescription',
  },
];

export const DistributionSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  channelErrors,
  channelLimitError,
  channelLimitLoading = false,
  accessCodeError = false,
  dataTestId = DISTRIBUTION_SECTION_TEST_ID,
}: DistributionSectionProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useWindowSize(SM_VIEWPORT);
  const isBelowLgViewport = useWindowSize(LG_VIEWPORT);
  const cardSize: SelectionCardSize = isBelowLgViewport ? 's' : 'l';
  const toggleSize: ToggleSectionSize = isMobileViewport ? 's' : 'l';

  const accessCode = value.accessCode;
  const channelLimit = value.channelLimit;

  return (
    <SectionDisclosure
      title={t('common:visibility')}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={`${dataTestId}-disclosure`}>
      <section
        data-testid={dataTestId}
        className={classNames('flex flex-col gap-6 w-full py-4 md:py-6', {
          'cursor-not-allowed opacity-50 pointer-events-none': disabled,
        })}>
        <div className='flex flex-col gap-4'>
          <div
            role='radiogroup'
            aria-label={t('common:visibility')}
            className='flex flex-col gap-2 lg:flex-row lg:gap-4'>
            {MODE_OPTIONS.map((option) => (
              <SelectionCard
                key={option.mode}
                id={`${dataTestId}-mode-${option.mode}`}
                name={RADIO_GROUP_NAME}
                value={option.mode}
                icon={option.icon}
                title={t(option.titleKey)}
                description={t(option.descriptionKey)}
                selected={value.mode === option.mode}
                onSelect={() => onChange({ mode: option.mode })}
                size={cardSize}
                disabled={disabled}
                className='lg:flex-1'
              />
            ))}
          </div>
          <p data-testid={`${dataTestId}-admin-note`} className='text-body-s text-secondary'>
            {t('offers:distributionAdminNote')}
          </p>
        </div>

        {accessCode !== undefined && (
          <ToggleSection
            value={accessCode.enabled}
            onChange={(enabled) => onChange({ accessCode: { ...accessCode, enabled } })}
            label={t('offers:distributionAccessCodeLabel')}
            subLabel={t('offers:distributionAccessCodeSubLabel')}
            size={toggleSize}
            disabled={disabled}
            dataTestId={`${dataTestId}-access-code`}>
            <Input
              value={accessCode.code}
              onChange={(e) => onChange({ accessCode: { ...accessCode, code: e.currentTarget.value } })}
              disabled={disabled}
              hasError={accessCodeError}
              placeholder={t('offers:distributionAccessCodePlaceholder')}
            />
          </ToggleSection>
        )}

        {(channelLimit || channelLimitLoading) && (
          <ToggleSection
            value={channelLimit?.enabled ?? false}
            onChange={(enabled) => {
              if (channelLimit) onChange({ channelLimit: { ...channelLimit, enabled } });
            }}
            label={t('offers:distributionLimitPerChannelLabel')}
            subLabel={t('offers:distributionLimitPerChannelSubLabel')}
            size={toggleSize}
            loading={channelLimitLoading}
            disabled={disabled}
            dataTestId={`${dataTestId}-channel-limit`}>
            {channelLimit && (
              <ChannelLimitFields
                value={channelLimit}
                onChange={(next) => onChange({ channelLimit: next })}
                channelErrors={channelErrors}
                channelLimitError={channelLimitError}
                disabled={disabled}
                labelClassName='text-body-s'
                dataTestId={`${dataTestId}-channel-limit-fields`}
              />
            )}
          </ToggleSection>
        )}
      </section>
    </SectionDisclosure>
  );
};
